/**
 * Guest banner component.
 *
 * Prompts guest users to login to save their progress.
 * Can be dismissed and won't show again (stored in preferences).
 */

import { X, Cloud } from 'lucide-react';
import { observer } from 'mobx-react-lite';

import { LoginButton } from './LoginButton';

import { Button } from '@/components/ui/Button';
import { useStores } from '@/stores/StoreContext';

/**
 * Guest banner that prompts users to log in.
 *
 * Shows only for guest users who haven't dismissed it.
 */
export const GuestBanner = observer(function GuestBanner(): React.ReactElement | null {
  const { authStore, preferencesStore } = useStores();

  // Only show for guest users who haven't dismissed the banner
  // Don't show for: authenticated users, unauthenticated users (before choice), or loading
  const isGuest = authStore.status === 'guest' || authStore.user?.isGuest === true;

  if (!isGuest || !preferencesStore.showGuestBanner) {
    return null;
  }

  const handleDismiss = (): void => {
    preferencesStore.dismissGuestBanner();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-abyss-dark/95 backdrop-blur-sm border-b border-primary/20">
      <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-2">
        <div className="flex items-center gap-3">
          <Cloud className="h-5 w-5 text-primary" />
          <p className="text-sm">
            <span className="font-medium">Playing as Guest.</span>{' '}
            <span className="text-muted-foreground">
              Sign in to save your progress across devices.
            </span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <LoginButton size="sm" variant="default" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={handleDismiss}
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
});
